import type { ConfigItem } from '../common/types'
import { DuplicateItemIdError, OAuthSetupError, RealtimeSetupError, UnknownMethodError } from '../errors'
import { ActionMethod } from './actions/action-method'
import { ExecutionContext, type ExecutionContextOptions } from './context/execution-context'
import { DictionaryMethod } from './dictionaries/dictionary-method'
import { DynamicParamsLoaderMethod, SampleResultLoaderMethod } from './loaders/loader-method'
import type { LoaderOwner } from './loaders/loader-owner'
import type { ExtensionMethod } from './method'
import type { SharedExtensionDefinition, SharedExtensionMethod, SharedExtensionServiceInfo } from './model/model-types'
import { toConfigItems } from './model/params-model'
import { OAuth2 } from './oauth/oauth2'
import type { OAuth2Props } from './oauth/types'
import type { DictionaryRef } from './params'
import { ConfigParams, encodeParams } from './params'
import { PollingTriggers } from './polling-triggers/polling-triggers'
import { PollingTriggerMethod } from './polling-triggers/trigger-method'
import type { PollingTriggerProps } from './polling-triggers/types'
import { RealtimeSetup } from './realtime-triggers/realtime-setup'
import { RealtimeTriggerMethod } from './realtime-triggers/trigger-method'
import type { RealtimeSetupProps, RealtimeTriggerProps } from './realtime-triggers/types'
import { SystemMethod, type SystemMethodSpec } from './system-method'
import type { ActionProps, DictionaryProps, ExtensionProps } from './types'

// One registered extension: everything a service declared, and everything the runner does with it. The
// composition root — `Flowrunner.createExtension(props)` builds one, the registry stores it, and every
// caller (definition build, config resolution, method dispatch) goes through it rather than reaching into a
// bag of Maps.
//
// Registration happens once, while the service module is being `require`d, and is validated as it goes: ids
// must be unique across ALL item kinds, a polling trigger must declare exactly one mechanism, and the
// opt-in features can only be set up once. An authoring mistake therefore surfaces from the first call the
// console or CLI makes against the extension, naming what has to be fixed.

export class Extension {
  readonly #props: ExtensionProps
  readonly #config: ConfigParams
  readonly #methods = new Map<string, ExtensionMethod>()
  readonly #pollingTriggers = new PollingTriggers(this)
  readonly #realtimeTriggers = new Map<string, RealtimeTriggerMethod>()
  readonly #usedIds = new Set<string>()
  #oauth?: OAuth2
  #realtime?: RealtimeSetup

  constructor(props: ExtensionProps) {
    this.#props = props
    // Eager: `configItems` is validated as the extension registers, like every other declared slot. Leaving
    // it lazy would let a bad one hide behind handlers that never happen to read config.
    this.#config = ConfigParams.of(props.configItems, { serviceId: props.id, kind: 'extension', slot: 'configItems' })
  }

  get id(): string {
    return this.#props.id
  }

  get props(): ExtensionProps {
    return this.#props
  }

  // The declared `configItems`, mapped once per extension rather than per invocation.
  get config(): ConfigParams {
    return this.#config
  }

  get oauth(): OAuth2 | undefined {
    return this.#oauth
  }

  get realtime(): RealtimeSetup | undefined {
    return this.#realtime
  }

  realtimeTriggers(): RealtimeTriggerMethod[] {
    return [...this.#realtimeTriggers.values()]
  }

  findRealtimeTrigger(id: string): RealtimeTriggerMethod | undefined {
    return this.#realtimeTriggers.get(id)
  }

  get pollingTriggers(): PollingTriggers {
    return this.#pollingTriggers
  }

  // The extension-level colours every item falls back to.
  get appearanceColors(): [string, string] {
    const [start = '', end = ''] = this.props.appearanceColors ?? []

    return [start, end]
  }

  // ── authoring-time registration ─────────────────────────────────────────────

  registerDictionary(props: DictionaryProps): DictionaryRef {
    const dictionary = new DictionaryMethod(this, props)

    this.#addMethod(dictionary, 'dictionary')

    // The ref threaded into `.dictionary(ref)` on a param; carries the auto-derived dependsOn.
    return { id: props.id, dependsOn: dictionary.dependsOn }
  }

  registerAction(props: ActionProps): void {
    const action = new ActionMethod(this, props)

    this.#addMethod(action, 'action')

    this.#registerSampleResultLoader(action, 'sample-result loader')

    // `dynamicParams` → a PARAM_SCHEMA_DEFINITION method that resolves extra param fields from criteria.
    if (action.hasDynamicParams()) {
      this.#addMethod(new DynamicParamsLoaderMethod(this, action), 'dynamic params loader')
    }
  }

  registerPollingTrigger(props: PollingTriggerProps): void {
    // Claim the id before constructing, so a duplicate id is reported ahead of a bad mechanism.
    this.#claimId(props.id, 'polling trigger')

    const trigger = new PollingTriggerMethod(this, props)

    this.#putMethod(trigger)
    this.#pollingTriggers.add(trigger)
    this.#registerSampleResultLoader(trigger, 'sample-result loader')
  }

  registerRealtimeTrigger(props: RealtimeTriggerProps): void {
    this.#claimId(props.id, 'realtime trigger')

    const trigger = new RealtimeTriggerMethod(this, props)

    this.#putMethod(trigger)
    this.#realtimeTriggers.set(trigger.id, trigger)
    this.#registerSampleResultLoader(trigger, 'sample-result loader')
  }

  setupRealtime(props: RealtimeSetupProps): void {
    if (this.#realtime) {
      throw new RealtimeSetupError(this.id, 'setupRealtimeTriggers(...) called more than once')
    }

    // The setup validates itself and publishes its SYSTEM methods as it is constructed.
    this.#realtime = new RealtimeSetup(this, props)
  }

  setupOAuth2(props: OAuth2Props): void {
    if (this.#oauth) {
      throw new OAuthSetupError(this.id, 'setupOauth2(...) called more than once')
    }

    // Like RealtimeSetup, it publishes its SYSTEM methods as it is constructed.
    this.#oauth = new OAuth2(this, props)
  }

  // Publish a feature's runtime-owned methods. Called by each subsystem as it is set up, so a SYSTEM method
  // exists exactly when the feature behind it does — and an id that collides with an author's own item is
  // caught here, loudly, instead of one silently shadowing the other.
  registerSystemMethods(specs: SystemMethodSpec[]): void {
    for (const spec of specs) {
      this.#addMethod(new SystemMethod(this, spec), 'system method')
    }
  }

  // ── runtime ─────────────────────────────────────────────────────────────────

  // Every method this extension publishes, in registration order.
  methods(): ExtensionMethod[] {
    return [...this.#methods.values()]
  }

  findMethod(name: string): ExtensionMethod | undefined {
    return this.#methods.get(name)
  }

  getMethod(name: string): ExtensionMethod {
    const method = this.findMethod(name)

    if (!method) {
      throw new UnknownMethodError(this.id, name)
    }

    return method
  }

  createContext(options: ExecutionContextOptions = {}): ExecutionContext {
    return new ExecutionContext(this, options)
  }

  // Dispatch: find the method, let it check its own input, run it. Every kind — action, dictionary,
  // generated loader, SYSTEM seam — arrives here through the same three steps.
  async run(methodName: string, methodData: Record<string, unknown>, ctx: ExecutionContext): Promise<unknown> {
    const method = this.getMethod(methodName)

    return method.execute(method.validate(methodData ?? {}), ctx)
  }

  // ── output model ────────────────────────────────────────────────────────────

  toConfigItems(): ConfigItem[] {
    return toConfigItems(this.config)
  }

  toServiceInfo(): SharedExtensionServiceInfo {
    return {
      serviceClassName: this.props.id,
      integrationName: this.props.name,
      integrationIcon: this.props.logo ?? null,
      integrationTriggersScope: this.#realtime?.scope ?? null,
      requireOAuth: this.#oauth ? true : undefined,
    }
  }

  /**
   * The published model — the v2 analogue of jsdoc-parser's `parseJsDocToServiceJson`, and the same
   * `{ serviceInfo, methodsMap }` shape the console consumes for a v1 service.
   *
   * Every method describes itself, so this is one pass with no per-kind branches: what a service publishes
   * is exactly what it registered.
   */
  toDefinition(): SharedExtensionDefinition {
    const methodsMap: Record<string, SharedExtensionMethod> = {}

    for (const method of this.methods()) {
      if (method.published) {
        methodsMap[method.name] = method.toModel()
      }
    }

    return {
      runnerVersion: 'v2', //TODO: temp
      serviceInfo: this.toServiceInfo(),
      methodsMap,
      configSchema: encodeParams(this.config.scheme),
    }
  }

  // ── queries ─────────────────────────────────────────────────────────────────

  // The `oauth2Scopes` every registered method declares, in registration order. The base set and the
  // dedupe live with the OAuth subsystem that consumes them (see OAuth2.#composeScopes).
  declaredOAuthScopes(): string[] {
    return this.methods().flatMap(method => method.oauth2Scopes)
  }

  // ── private ─────────────────────────────────────────────────────────────────

  // Every registered item is keyed by `id` (= the v1 method name, frozen for back-compat), and the ids must
  // be unique across ALL kinds — including the loader methods the runtime generates from them.
  #claimId(id: string, kind: string): void {
    if (!id) {
      throw new Error(`[flow-extension:${ this.id }] a ${ kind } was registered without an id`)
    }

    if (this.#usedIds.has(id)) {
      throw new DuplicateItemIdError(this.id, id, kind)
    }

    this.#usedIds.add(id)
  }

  #addMethod(method: ExtensionMethod, kind: string): void {
    this.#claimId(method.name, kind)
    this.#putMethod(method)
  }

  #putMethod(method: ExtensionMethod): void {
    this.#methods.set(method.name, method)
  }

  // A loader `result` fn → a SAMPLE_RESULT_LOADER method. Actions and both trigger kinds can declare one,
  // and each satisfies LoaderOwner directly.
  #registerSampleResultLoader(owner: LoaderOwner, kind: string): void {
    if (owner.result.isLoader) {
      this.#addMethod(new SampleResultLoaderMethod(this, owner), kind)
    }
  }

}
